/**
 * Auto-Increment Asset ID Utilities
 *
 * Provides logic to suggest the next sequential Client ID for a given asset type,
 * based on existing assets already loaded in serviceData.
 */

/**
 * Maps asset type names to their Client ID field names.
 * Note: TrafficSign maps to ClientSignID (not ClientTrafficSignID).
 */
const ASSET_ID_FIELD_MAP = {
    Hydrant: "ClientHydrantID",
    Manhole: "ClientManholeID",
    Valve: "ClientValveID",
    TrafficSign: "ClientSignID",
    WaterMeter: "ClientWaterMeterID",
    WaterLine: "ClientWaterLineID",
    SewerLine: "ClientSewerLineID",
};

/**
 * Attempt to parse an ID string into a prefix and a numeric portion.
 * Examples:
 *   "HYD-0046" => { prefix: "HYD-", number: 46, padding: 4 }
 *   "MH-123"   => { prefix: "MH-", number: 123, padding: 3 }
 *   "0089"     => { prefix: "", number: 89, padding: 4 }
 *   "42"       => { prefix: "", number: 42, padding: 0 }  (no leading zeros => no padding)
 *   "V-0089"   => { prefix: "V-", number: 89, padding: 4 }
 *
 * Returns null if the string has no numeric portion at all.
 */
function parseIdPattern(idStr) {
    if (idStr == null) return null;
    const str = String(idStr).trim();
    if (!str) return null;

    // Match: optional non-digit prefix, then trailing digits
    const match = str.match(/^(.*?)(\d+)$/);
    if (!match) return null;

    const prefix = match[1];
    const numericStr = match[2];
    const number = parseInt(numericStr, 10);
    // padding = length of the numeric string (preserves leading zeros)
    // Only count as padded if there are leading zeros or length > digit count
    const padding = numericStr.length;

    return { prefix, number, padding };
}

/**
 * Given the next number and the detected pattern, format the new ID string.
 * Preserves the prefix and zero-padding from the original pattern.
 */
function formatNextId(prefix, nextNumber, padding) {
    const numStr = String(nextNumber);
    // Pad with leading zeros if the original pattern had a fixed width
    const paddedNum = padding > numStr.length
        ? numStr.padStart(padding, "0")
        : numStr;
    return `${prefix}${paddedNum}`;
}

/**
 * Compute the next suggested Asset ID based on existing data.
 *
 * @param {Array} serviceData - All loaded assets (point + line combined)
 * @param {string} assetType  - The asset type name (e.g., "Hydrant", "TrafficSign")
 * @returns {{ fieldName: string, suggestedId: string } | null}
 *          Returns null if the asset type is unknown.
 */
export function getNextAssetId(serviceData, assetType) {
    const fieldName = ASSET_ID_FIELD_MAP[assetType];
    if (!fieldName) {
        return null;
    }

    if (!serviceData || !Array.isArray(serviceData) || serviceData.length === 0) {
        return { fieldName, suggestedId: "1" };
    }

    // Collect all existing IDs for this asset type
    const existingIds = serviceData
        .map((item) => item[fieldName])
        .filter((id) => id != null && String(id).trim() !== "");

    if (existingIds.length === 0) {
        return { fieldName, suggestedId: "1" };
    }

    // Parse all IDs into structured patterns
    const parsed = existingIds
        .map((id) => ({ raw: String(id), ...parseIdPattern(id) }))
        .filter((p) => p.number != null);

    if (parsed.length === 0) {
        // No numeric IDs found at all — start fresh
        return { fieldName, suggestedId: "1" };
    }

    // Group by prefix to find the most common pattern
    const prefixCounts = {};
    for (const p of parsed) {
        const key = p.prefix;
        if (!prefixCounts[key]) {
            prefixCounts[key] = { count: 0, maxNumber: 0, padding: p.padding };
        }
        prefixCounts[key].count += 1;
        if (p.number > prefixCounts[key].maxNumber) {
            prefixCounts[key].maxNumber = p.number;
            prefixCounts[key].padding = p.padding;
        }
    }

    // Pick the prefix with the most entries (the dominant pattern)
    let dominantPrefix = "";
    let dominantInfo = { count: 0, maxNumber: 0, padding: 0 };
    for (const [prefix, info] of Object.entries(prefixCounts)) {
        if (info.count > dominantInfo.count) {
            dominantPrefix = prefix;
            dominantInfo = info;
        }
    }

    // Compute the next number
    const nextNumber = dominantInfo.maxNumber + 1;
    let suggestedId = formatNextId(dominantPrefix, nextNumber, dominantInfo.padding);

    // Duplicate check — ensure the suggested ID doesn't already exist
    const existingIdSet = new Set(existingIds.map((id) => String(id).trim()));
    let safetyCounter = 0;
    let currentNumber = nextNumber;
    while (existingIdSet.has(suggestedId) && safetyCounter < 1000) {
        currentNumber += 1;
        suggestedId = formatNextId(dominantPrefix, currentNumber, dominantInfo.padding);
        safetyCounter += 1;
    }

    return { fieldName, suggestedId };
}

// Also export the field map for potential reuse
export { ASSET_ID_FIELD_MAP };
